import { useEffect, useState } from 'react';
import { Bot, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { runnerApi, type RunnerEnablement } from '../../services/api';
import { feature } from '../../services/telemetry';

// Ways runner sessions (PO92-225): the project owner (project:edit) switches runners on with
// limits, or withdraws them -- which revokes every live runner in the project. The server
// checks the permission; this is only the form.
const KINDS = ['ask', 'item-request', 'handoff', 'note', 'reply', 'decision-request'];

export default function RunnerSettingsModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [en, setEn] = useState<RunnerEnablement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    runnerApi.get(projectId).then(setEn).catch((e) => setError(e?.message || 'Could not load runner settings'));
  }, [projectId]);

  const put = <K extends keyof RunnerEnablement>(k: K, v: RunnerEnablement[K]) => setEn((e) => (e ? { ...e, [k]: v } : e));

  const save = async () => {
    if (!en) return;
    if (!en.enabled && !confirm('Turning runners off ends every live runner session in this project. Continue?')) return;
    setBusy(true);
    try {
      const saved = await runnerApi.set({
        projectId, enabled: en.enabled, mode: en.mode, maxConcurrent: en.maxConcurrent,
        dailyTokenBudget: en.dailyTokenBudget, requestKinds: en.requestKinds, push: en.push,
      });
      setEn(saved);
      feature('projects.runner-settings');
      toast.success(saved.enabled ? 'Runners enabled' : 'Runners turned off');
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save runner settings');
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Bot className="text-accent" size={20} />
          <h2 className="text-base font-semibold flex-1">Runner sessions</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X size={16} /></button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Lets Ways start a Claude Code session on the server for a request on one work item, acting for you,
          when nobody's session is open. Runners never decide, vote or delete, and close items only by request.
        </p>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {!en && !error && <p className="text-sm text-gray-400">Loading…</p>}
        {en && (
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2 font-medium">
              <input type="checkbox" checked={en.enabled} onChange={(e) => put('enabled', e.target.checked)} />
              Runners enabled for this project
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-500">Mode</span>
                <select className={field} value={en.mode} onChange={(e) => put('mode', e.target.value as RunnerEnablement['mode'])}>
                  <option value="on-demand">On demand</option>
                  <option value="standby">Standby (warm pod)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Max concurrent (1–10)</span>
                <input type="number" min={1} max={10} className={field} value={en.maxConcurrent}
                  onChange={(e) => put('maxConcurrent', Math.max(1, Math.min(10, Number(e.target.value) || 1)))} />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-gray-500">Daily token budget (blank = none)</span>
              <input type="number" min={0} step={100000} className={field} value={en.dailyTokenBudget ?? ''}
                onChange={(e) => put('dailyTokenBudget', e.target.value === '' ? null : Number(e.target.value))} />
            </label>
            <div>
              <span className="text-xs text-gray-500">Request kinds a runner takes (none ticked = any)</span>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                {KINDS.map((k) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={en.requestKinds.includes(k)}
                      onChange={(e) => put('requestKinds', e.target.checked ? [...en.requestKinds, k] : en.requestKinds.filter((x) => x !== k))} />
                    {k}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={en.push} onChange={(e) => put('push', e.target.checked)} />
              Allow runners to push a <code>runner/&lt;item&gt;</code> branch
            </label>
            {en.approvedAt && (
              <p className="text-xs text-gray-400">Last set {new Date(en.approvedAt).toLocaleString()}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg">Cancel</button>
              <button onClick={save} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg disabled:opacity-40">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
